import { useRef } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_CONTACT_CONSENT } from '../../../lib/graphql/queries/contacts';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { selectConsentRead } from '../../../components/features/contacts/consent/consentViewState';
import type { GQLContactConsent } from './useContact';

/** One row of the append-only consent ledger (SPEC-2314). */
export interface GQLConsentEvent {
  consentEventId: string;
  channel: string;
  eventType: string;
  source: string | null;
  consentState: boolean | null;
  /** Spanner user_id for events written after SPEC-2314; older rows hold a Firebase uid. */
  actor: string | null;
  reason: string | null;
  occurredAt: string | null;
  createdAt: string | null;
}

/** Paginated envelope the resolver returns for the audit trail. */
interface GQLConsentHistoryPage {
  items: GQLConsentEvent[];
  total: number;
  hasMore: boolean;
}

interface GetContactConsentData {
  contact: {
    contactId: string;
    consent: GQLContactConsent | null;
    consentHistory: GQLConsentHistoryPage | null;
  } | null;
}

interface UseContactConsentGQLProps {
  contactId: string;
  enabled?: boolean;
  limit?: number;
  /** Ledger channel to filter history by — `voice` | `sms` | `email`. Omit for all. */
  channel?: string | null;
  /** ISO bounds on the history, inclusive. Omit either side for open-ended. */
  dateFrom?: string | null;
  dateTo?: string | null;
  offset?: number;
}

/**
 * Per-channel consent state and its audit trail in a single query.
 *
 * Both halves come from one response, so the status cards can never disagree
 * with the timeline. Fetched on demand by the Consent tab rather than riding
 * on GET_CONTACT, which every contact view pays for.
 */
export const useContactConsentGQL = ({
  contactId,
  enabled = true,
  limit,
  channel = null,
  dateFrom = null,
  dateTo = null,
  offset = 0,
}: UseContactConsentGQLProps) => {
  const { organization } = useOrganizationStore();
  const organizationId = organization?.id;
  const identity = `${organizationId ?? ''}:${contactId}`;
  const lastRead = useRef<{ identity: string; data: GetContactConsentData } | null>(null);

  const { data, loading, error, refetch } = useQuery<GetContactConsentData>(
    GET_CONTACT_CONSENT,
    {
      variables: {
        organizationId: organizationId ?? '',
        contactId,
        channel,
        dateFrom,
        dateTo,
        limit,
        offset,
      },
      skip: !enabled || !contactId || !organizationId,
      fetchPolicy: 'cache-and-network',
    }
  );

  // Filter and paging changes may temporarily have no response. Keep the last
  // result only for this contact and org: showing another contact's old cards
  // here could make a toggle write using the new contact id and old standing.
  const selected = selectConsentRead(identity, contactId, data, lastRead.current);
  lastRead.current = selected.last;
  const current = selected.current;

  const page = current?.contact?.consentHistory;

  return {
    consent: current?.contact?.consent ?? null,
    history: page?.items ?? [],
    /** Total matching events server-side, not just the page in hand. */
    historyTotal: page?.total ?? 0,
    hasMoreHistory: page?.hasMore ?? false,
    loading,
    error: error as Error | null | undefined,
    refetch,
  };
};
